import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";
import Paper from "@mui/material/Paper";
import { cssProperties } from "../utils/commonCssProperties";

const cellStyle = {
  overflow: "hidden",
  wordWrap: "break-word",
  whiteSpace: "nowrap",
  textAlign: "center",
};

const headerCellStyle = {
  ...cellStyle,
  fontWeight: "bold",
};

/**
 * columnData items may include an optional `render(value, row)` function.
 * When provided it replaces the raw cell value with whatever JSX it returns.
 *
 * Server-side pagination:
 *   Pass serverSide=true and control page/rowsPerPage/onPageChange/onRowsPerPageChange
 *   from the parent.  totalCount must be the full dataset size (used by MUI for page math).
 */
export default function ReconcilationReportTable({
  tableData = [],
  columnData = [],
  // server-side pagination props
  serverSide = false,
  totalCount = 0,
  page: externalPage = 0,         // 0-based (MUI convention)
  rowsPerPage: externalRPP = 50,
  onPageChange,
  onRowsPerPageChange,
}) {
  // ── client-side pagination state (used when serverSide=false) ──────────────
  const [clientPage, setClientPage] = React.useState(0);
  const [clientRPP, setClientRPP] = React.useState(10);

  React.useEffect(() => {
    if (!serverSide) setClientPage(0);
  }, [tableData, serverSide]);

  // ── unified pagination values ──────────────────────────────────────────────
  const page         = serverSide ? externalPage  : clientPage;
  const rowsPerPage  = serverSide ? externalRPP   : clientRPP;
  const count        = serverSide ? totalCount     : tableData.length;

  const handleChangePage = (_, newPage) => {
    if (serverSide) {
      onPageChange?.(_, newPage);
    } else {
      setClientPage(newPage);
    }
  };

  const handleChangeRowsPerPage = (event) => {
    if (serverSide) {
      onRowsPerPageChange?.(event);
    } else {
      setClientRPP(parseInt(event.target.value, 10));
      setClientPage(0);
    }
  };

  const rows = Array.isArray(tableData) ? tableData : [];

  const paginatedRows = serverSide
    ? rows
    : rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <TableContainer
      component={Paper}
      sx={{
        background: "none",
        border: `1px solid ${cssProperties.bordercolor.primary2blue}`,
        boxShadow: "none",
      }}
    >
      <Table aria-label="reconciliation report table">
        {/* ================= HEADER ================= */}
        <TableHead>
          <TableRow>
            {columnData.map((col, index) => (
              <TableCell
                key={index}
                align="center"
                sx={{
                  color: cssProperties.bordercolor.primary2blue,
                  borderRight: `1px solid ${cssProperties.bordercolor.primary2blue}`,
                  ...headerCellStyle,
                }}
              >
                {col.name}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        {/* ================= BODY ================= */}
        <TableBody>
          {paginatedRows.length > 0 ? (
            paginatedRows.map((row, rowIndex) => (
              <TableRow key={rowIndex}>
                {columnData.map((col, colIndex) => (
                  <TableCell
                    key={`${rowIndex}-${colIndex}`}
                    align="center"
                    sx={{
                      borderRight: `1px solid ${cssProperties.bordercolor.primary2blue}`,
                      ...cellStyle,
                    }}
                  >
                    {col.render
                      ? col.render(row[col.dataIndex], row)
                      : (row[col.dataIndex] ?? "-")}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell
                colSpan={columnData.length}
                sx={{
                  textAlign: "center",
                  color: cssProperties.bordercolor.primary2blue,
                  fontWeight: "bold",
                }}
              >
                No Result Found
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {/* ================= PAGINATION ================= */}
      <TablePagination
        component="div"
        count={count}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[10, 25, 50, 100]}
      />
    </TableContainer>
  );
}
